import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useGetSingleUnifiedNewsEntryQuery, useGetUnifiedNewsEntriesQuery } from '../../../api/feed'
import { useGameUpdateImpactsAccessCheck } from '../../account/hooks/roleHooks'
import { useCurrentUserLanguage } from '../../account/hooks/useCurrentUserLanguage'
import { SubgenreMap } from '../../account/types/UserSettings'
import featureService from '../../feature/services/FeatureService'
import { useResolvedGameNames } from '../../game/hooks/useResolvedGameNames'
import { collectNewsEntryGameIds, overlayNewsEntryGameNames } from '../../game/util/overlayResolvedGameNames'
import { useDateTimeFormatter } from '../../live-events/hooks/useDateTimeFormatter'
import { NewsListPagination } from '../types/NewsListPagination'
import { NewsEntryFeature, NewsEntryScreenshot, NewsEntryScreenshotFeature, NewsEntryType, UnifiedNewsEntry } from '../types/UnifiedNewsEntry'

type UnifiedNewsEntriesHookParams = {
  marketIso: string
  language: string
  selectedSubgenresMap: SubgenreMap
  newsfeedTypes: Array<string>
  limit: number
  pagination: NewsListPagination
}
export function useUnifiedNewsEntries({ marketIso, language, selectedSubgenresMap, newsfeedTypes, limit, pagination }: UnifiedNewsEntriesHookParams) {
  // use separate state to collect all paged news entries as they are loaded
  const [allNewsEntries, setAllNewsEntries] = useState<UnifiedNewsEntry[]>([])
  const [lastPageReached, setLastPageReached] = useState(false)

  const conventionalSubgenres = Object.keys(selectedSubgenresMap)
  // `currentData` (not `data`) is intentional: `data` retains the previous page's response while a
  // new page is being fetched, which made the pagination effect append stale page-1 data onto
  // itself on "Show more" click, flag "no new entries" and permanently hide the button (GR-2974).
  // `currentData` is undefined while fetching for new args, so the effect only runs with data that
  // matches the current pagination.
  const {
    currentData: { newsData: unifiedNewsEntries, lastEvents: unifiedNewsEntriesLastEvents } = {},
    isLoading: unifiedNewsEntriesLoading,
    isFetching: unifiedNewsEntriesFetching,
    error: unifiedNewsEntriesError,
  } = useGetUnifiedNewsEntriesQuery({ marketIso, language, conventionalSubgenres, types: newsfeedTypes, limit, pagination })

  // A value derived from the query args, not from a fresh object reference. `pagination` is
  // recreated on every market/preference switch, so keying effects on the object itself caused a
  // spurious reset that could clear the list without repopulating it when RTK Query served a
  // cached (stable-reference) response (e.g. switching back to a previously loaded market).
  const isFirstPage = pagination.stickyEditorials === 0 && pagination.other === 0

  useEffect(() => {
    // First page (initial load, market switch, or preference change): replace the accumulated
    // list with the current first-page results. Reading directly from the query response keeps
    // reset and repopulation atomic, so a cached response is never left showing an empty feed.
    if (isFirstPage) {
      setAllNewsEntries(unifiedNewsEntries ?? [])
      setLastPageReached(!!unifiedNewsEntriesLastEvents)
      return
    }

    // "Show more" pagination: append the newly fetched page to what we already have.
    if (unifiedNewsEntries) {
      setAllNewsEntries((previousValue) => {
        const newsEntryIds: { [entryId: string]: boolean } = {}
        const combined = [...previousValue, ...unifiedNewsEntries]

        const uniqueNewsEntries = combined.filter((entry) => {
          const id = entry.feedEntry?.id
          if (!id) return false
          if (newsEntryIds[id]) return false
          newsEntryIds[id] = true
          return true
        })

        const noNewEntriesAdded = uniqueNewsEntries.length === previousValue.length

        // Set explicitly (not just to true) so a successful append that is not the last page
        // restores the "Show more" button (GR-2974).
        setLastPageReached(noNewEntriesAdded || !!unifiedNewsEntriesLastEvents)

        return uniqueNewsEntries
      })
    }
  }, [unifiedNewsEntries, unifiedNewsEntriesLastEvents, isFirstPage])

  const newsEntryGameIds = useMemo(() => collectNewsEntryGameIds(allNewsEntries), [allNewsEntries])
  const { namesByGameId, isLoading: isLoadingNames } = useResolvedGameNames(newsEntryGameIds)
  const newsEntriesWithUserLanguageNames = useMemo(() => overlayNewsEntryGameNames(allNewsEntries, namesByGameId), [allNewsEntries, namesByGameId])
  const waitingForFirstPageNames = isFirstPage && isLoadingNames

  return {
    unifiedNewsEntries: waitingForFirstPageNames ? [] : newsEntriesWithUserLanguageNames,
    unifiedNewsEntriesLoading: unifiedNewsEntriesLoading || unifiedNewsEntriesFetching || waitingForFirstPageNames,
    unifiedNewsEntriesError,
    unifiedNewsEntriesLastEvents: lastPageReached,
  }
}

export function useUnifiedNewsEntry(mainMarketIso: string, newsEntryId: string, language: string) {
  const {
    data: unifiedNewsEntry,
    isLoading: unifiedNewsEntryLoading,
    isFetching: unifiedNewsEntryFetching,
    error: unifiedNewsEntryError,
  } = useGetSingleUnifiedNewsEntryQuery({ id: newsEntryId, language })
  const newsEntryGameIds = useMemo(() => collectNewsEntryGameIds(unifiedNewsEntry ? [unifiedNewsEntry] : []), [unifiedNewsEntry])
  const { namesByGameId, isLoading: isLoadingNames } = useResolvedGameNames(newsEntryGameIds)
  const newsEntryWithUserLanguageName = useMemo(() => {
    if (!unifiedNewsEntry || isLoadingNames) {
      return undefined
    }

    return overlayNewsEntryGameNames([unifiedNewsEntry], namesByGameId)[0]
  }, [unifiedNewsEntry, namesByGameId, isLoadingNames])

  return {
    unifiedNewsEntry: newsEntryWithUserLanguageName,
    unifiedNewsEntryLoading: unifiedNewsEntryLoading || unifiedNewsEntryFetching || isLoadingNames,
    unifiedNewsEntryError,
  }
}

type NewsCardScreenshotsHookParams = {
  newsEntry: UnifiedNewsEntry
}
export const useNewsCardScreenshots = ({ newsEntry }: NewsCardScreenshotsHookParams) => {
  const entryType = newsEntry.feedEntry.type
  const gameUpdateScreenshots: NewsEntryScreenshot[] = newsEntry.feedEntry.entryData.screenshots || []
  const liveEventScreenshots = newsEntry.feedEntry.entryData.screenshotUrls || []
  let versionScreenshots: string[] = []

  if (gameUpdateScreenshots.length === 0 && (entryType === NewsEntryType.MajorGameRelease || entryType === NewsEntryType.SoftLaunchNonAnalyzed)) {
    versionScreenshots = newsEntry.versionInfo?.screenshots.map((url: string) => url) || []
  }

  return { gameUpdateScreenshots, versionScreenshots, liveEventScreenshots }
}

type NewsCardFeaturesHookParams = {
  newsEntry: UnifiedNewsEntry
}
export const useNewsCardFeatures = ({ newsEntry }: NewsCardFeaturesHookParams): [NewsEntryFeature[], NewsEntryScreenshotFeature[]] => {
  const entryType = newsEntry.feedEntry.type
  let changedFeatures: Array<NewsEntryFeature> = []
  let updatedFeatures: Array<NewsEntryScreenshotFeature> = []

  if (entryType === NewsEntryType.GpsUpdate || entryType === NewsEntryType.PerformanceChangeRevenue || entryType === NewsEntryType.PerformanceChangeDownload) {
    if (newsEntry.feedEntry.entryData.features && newsEntry.feedEntry.entryData.features.length) {
      changedFeatures = featureService.getFilteredFeaturesForNewsEntryFeatures(newsEntry.feedEntry.entryData.features)
    }

    if (newsEntry.versionInfo && newsEntry.feedEntry.entryData.screenshots && newsEntry.feedEntry.entryData.screenshots.length > 0) {
      const filterFeatures = featureService.getUpdatedFeaturesFromNewsEntryScreenshots(changedFeatures, newsEntry.feedEntry.entryData.screenshots)
      updatedFeatures = filterFeatures.filter((filterFeature) => {
        return !changedFeatures.find((changedFeature) => changedFeature.featureName === filterFeature.featureName_translation)
      })
    }
  }

  return [changedFeatures, updatedFeatures]
}

type NewsCardTitleHookParams = {
  newsEntry: UnifiedNewsEntry
}
export const useNewsCardTitle = ({ newsEntry }: NewsCardTitleHookParams) => {
  const { t } = useTranslation()
  const dateFormatter = useDateTimeFormatter()
  const currentUserLanguage = useCurrentUserLanguage()
  const entryType = newsEntry.feedEntry.type
  const entryData = newsEntry.feedEntry.entryData
  const gpsChange = Math.round((entryData.gpsChange || 0) * 10) / 10
  const rankValue = useMemo(() => {
    const rank = entryData.rank
    if (rank <= 10) {
      return 10
    } else if (rank <= 20) {
      return 20
    } else if (rank <= 50) {
      return 50
    } else if (rank <= 100) {
      return 100
    } else if (rank <= 200) {
      return 200
    } else if (rank > 200) {
      return 500
    }

    return 500
  }, [entryData.rank])

  let title = newsEntry.feedEntry.title || newsEntry.feedEntry.titles[currentUserLanguage]
  let subtitle: string | undefined = newsEntry.feedEntry.subtitle || newsEntry.feedEntry.subtitles[currentUserLanguage]

  switch (entryType) {
    case NewsEntryType.GpsNew:
    case NewsEntryType.GpsOutside:
      if (entryData.organizationId) {
        title = t('news-titles:first_analysis_own')
      } else if (rankValue > 200) {
        title = t('news-titles:first_analysis_out200')
      } else {
        title = t('news-titles:first_analysis_top100', { topListNumber: rankValue })
      }

      break

    case NewsEntryType.GpsUpdate:
      if (entryData.organizationId) {
        title = t('news-titles:updated_analysis_own')
      } else {
        title = t('news-titles:updated_analysis_public')
      }
      subtitle = t('news-titles:updated_analysis_subtitle', { change: gpsChange })
      break

    case NewsEntryType.GpsSystem:
      title = t('news-titles:updated_analysis_system', { gpsBefore: Math.round(entryData.gpsBefore || 0), gps: Math.round(entryData.gps || 0) })
      subtitle = t('news-titles:updated_analysis_system_subtitle')
      break

    case NewsEntryType.EntryTop10:
    case NewsEntryType.EntryTop20:
    case NewsEntryType.EntryTop50:
    case NewsEntryType.EntryTop100:
    case NewsEntryType.EntryTop200:
      title = t('news-titles:first_appearance_top_grossing', { topListNumber: rankValue })
      break
    case NewsEntryType.LiveEventNewEventType:
    case NewsEntryType.LiveEventAnalystNotes:
    case NewsEntryType.LiveEventTrackedGame:
      title = t(`news-titles:${entryType}`)
      break
    case NewsEntryType.Report:
      title = entryData.title?.[currentUserLanguage]
      break
  }

  if (entryType === NewsEntryType.MajorGameRelease || entryType === NewsEntryType.SoftLaunchNonAnalyzed) {
    if (newsEntry.versionInfo) {
      subtitle = t('common:released_with_date_label', {
        dateString: dateFormatter.format(newsEntry.versionInfo.releaseDate),
      })
    } else {
      subtitle = t('common:released_with_date_label', {
        dateString: dateFormatter.format(newsEntry.feedEntry.publishedAt),
      })
    }
  }

  if (entryData.gpsChange === 0) {
    subtitle = undefined
  }

  return [title, subtitle]
}

export const useRevenueAndDownloadsDataAccessCheck = (entryType: NewsEntryType) => {
  const hasAccessToGameUpdateImpacts = useGameUpdateImpactsAccessCheck()
  return (entryType === NewsEntryType.PerformanceChangeRevenue || entryType === NewsEntryType.PerformanceChangeDownload) && !hasAccessToGameUpdateImpacts
}
